import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, Send, Bot } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const SUGGESTIONS = [
  "I have $0 and 5 hours/week. What's the fastest path to my first $100?",
  "I'm decent at writing. Should I go affiliate or freelance?",
  "What's a realistic 30-day plan for dropshipping in 2026?",
  "How do I price myself as a beginner freelancer?",
];

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const endRef = useRef();

  useEffect(() => {
    api.get("/coach/history").then((r) => {
      setMessages(r.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (preset) => {
    const msg = preset || text;
    if (!msg.trim() || busy) return;
    setText("");
    const tempUser = { msg_id: `tmp_${Date.now()}`, role: "user", text: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tempUser]);
    setBusy(true);
    try {
      const { data } = await api.post("/coach/chat", { text: msg });
      setMessages((m) => [...m.filter((x) => x.msg_id !== tempUser.msg_id), data.user_msg, data.reply]);
    } catch (e) {
      setMessages((m) => [...m, { msg_id: `err_${Date.now()}`, role: "assistant", text: "Hmm, the coach is offline. Try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 md:px-8 flex flex-col h-[calc(100vh-4rem)]">
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-md bg-[#00E676] flex items-center justify-center">
              <Sparkles size={18} strokeWidth={2.5} className="text-black" />
            </div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">AI Money Coach</h1>
          </div>
          <p className="text-sm text-white/45 font-mono">Ask anything. Get a real, niche-specific plan.</p>
        </div>

        <div className="flex-1 surface rounded-xl p-5 overflow-y-auto mb-3" data-testid="coach-thread">
          {loading ? (
            <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="surface-elev rounded-xl h-20 shimmer" />)}</div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-md bg-[#00E676]/10 border border-[#00E676]/20 flex items-center justify-center mb-4">
                <Bot size={22} className="text-[#00E676]" />
              </div>
              <div className="font-heading font-bold text-xl mb-1">Hey — I'm your money coach.</div>
              <div className="text-sm text-white/55 mb-6 max-w-md">
                Tell me about your skills, time, and goal. I'll give you a step-by-step plan.
              </div>
              <div className="grid sm:grid-cols-2 gap-2 w-full max-w-xl">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`coach-suggestion-${i}`}
                    onClick={() => send(s)}
                    className="surface-elev rounded-md p-3 text-left text-xs text-white/70 hover:bg-white/[0.06] hover:text-white transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((m) => (
                <motion.div
                  key={m.msg_id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}
                  data-testid={`coach-msg-${m.msg_id}`}
                >
                  {m.role === "assistant" && (
                    <div className="w-8 h-8 rounded-md bg-[#00E676] flex items-center justify-center shrink-0">
                      <Bot size={14} strokeWidth={2.5} className="text-black" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-white text-black rounded-tr-sm"
                        : "bg-[#051F10] border border-[#00E676]/25 text-white/90 rounded-tl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </motion.div>
              ))}
              {busy && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-md bg-[#00E676] flex items-center justify-center shrink-0">
                    <Bot size={14} strokeWidth={2.5} className="text-black" />
                  </div>
                  <div className="bg-[#051F10] border border-[#00E676]/25 rounded-xl rounded-tl-sm px-4 py-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" style={{ animationDelay: "0.2s" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" style={{ animationDelay: "0.4s" }} />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="surface rounded-xl p-3 flex items-end gap-2"
        >
          <textarea
            data-testid="coach-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask anything about making money online…"
            rows={1}
            className="flex-1 bg-transparent resize-none focus:outline-none text-sm text-white placeholder:text-white/30 leading-relaxed py-2 px-2 max-h-32"
          />
          <button
            data-testid="coach-send"
            type="submit"
            disabled={busy || !text.trim()}
            className="cta-green rounded-md p-2.5 disabled:opacity-40"
          >
            <Send size={14} strokeWidth={2.5} />
          </button>
        </form>
      </div>
    </Layout>
  );
}
